import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { SubOrgRequest, SubOrgResponse } from '../../../@types/subOrganization/subOrganization.type';
import axiosInstance from '../../../utils/axios';

// ---------- Types ----------
interface RequestState<T = any> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

interface SubOrgState {
  list: RequestState<SubOrgResponse[]>;
  single: RequestState<SubOrgResponse>;
  create: RequestState<SubOrgResponse>;
  update: RequestState<SubOrgResponse>;
  delete: RequestState<string>;
  posConfigFields: RequestState<any[]>;
  posConfig: RequestState<Record<string, string>>;
  savePosConfig: RequestState<any>;
  syncPos: RequestState<any>;
  pullPos: RequestState<any>;
  pushPos: RequestState<any>;
}

// ---------- Initial State ----------
const initialRequestState = <T,>(): RequestState<T> => ({
  data: null,
  loading: false,
  error: null,
});

const initialState: SubOrgState = {
  list: initialRequestState<SubOrgResponse[]>(),
  single: initialRequestState<SubOrgResponse>(),
  create: initialRequestState<SubOrgResponse>(),
  update: initialRequestState<SubOrgResponse>(),
  delete: initialRequestState<string>(),
  posConfigFields: initialRequestState<any[]>(),
  posConfig: initialRequestState<Record<string, string>>(),
  savePosConfig: initialRequestState<any>(),
  syncPos: initialRequestState<any>(),
  pullPos: initialRequestState<any>(),
  pushPos: initialRequestState<any>(),
};

// ---------- Thunks ----------
const BASE_URL = '/api/v1/organizations';
const POS_URL = '/api/v1/pos';

export const fetchPosConfigFields = createAsyncThunk(
  'subOrgs/fetchPosConfigFields',
  async (posType: string, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`${POS_URL}/config-fields?type=${posType}`);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Fetch config fields failed');
    }
  }
);

export const savePosConfig = createAsyncThunk(
  'subOrgs/savePosConfig',
  async ({ orgId, config }: { orgId: string; config: any }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(`${POS_URL}/config/${orgId}`, config);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err?.message || (typeof err === 'string' ? err : 'Save config failed'));
    }
  }
);

export const startPosSync = createAsyncThunk(
  'subOrgs/startPosSync',
  async (orgId: string, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(`${POS_URL}/sync/start/${orgId}`);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err?.message || (typeof err === 'string' ? err : 'Start sync failed'));
    }
  }
);

export const pullFromPos = createAsyncThunk(
  'subOrgs/pullFromPos',
  async (orgId: string, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(`${POS_URL}/sync/pull/${orgId}`);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err?.message || (typeof err === 'string' ? err : 'Pull from POS failed'));
    }
  }
);

export const pushToPos = createAsyncThunk(
  'subOrgs/pushToPos',
  async (orgId: string, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(`${POS_URL}/sync/push/${orgId}`);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err?.message || (typeof err === 'string' ? err : 'Push to POS failed'));
    }
  }
);

export const fetchPosConfig = createAsyncThunk(
  'subOrgs/fetchPosConfig',
  async ({ orgId, posType }: { orgId: string; posType: string }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`${POS_URL}/config/${orgId}?type=${posType}`);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Fetch configuration failed');
    }
  }
);

export const fetchSubOrgsByParentID = createAsyncThunk(
  'subOrgs/fetchByParentID',
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`${BASE_URL}/parent/${id}/sub-orgs`);
      return res.data.content as SubOrgResponse[];
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Fetch failed');
    }
  }
);

export const fetchAllSubOrgs = createAsyncThunk(
  'subOrgs/fetchAll',
  async (pageable: { page: number; size: number; sort: string }, { rejectWithValue }) => {
    try {
      const { page, size, sort } = pageable;
      const res = await axiosInstance.get(`${BASE_URL}/sub-orgs?page=${page}&size=${size}&sort=${sort}`);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Fetch failed');
    }
  }
);

export const fetchSubOrgById = createAsyncThunk(
  'subOrgs/fetchById',
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`${BASE_URL}/${id}`);
      return res.data as SubOrgResponse;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Fetch failed');
    }
  }
);

export const createSubOrg = createAsyncThunk(
  'subOrgs/create',
  async (formData: FormData, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(BASE_URL, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return res.data as SubOrgResponse;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Create failed');
    }
  }
);

export const updateSubOrg = createAsyncThunk(
  'subOrgs/update',
  async ({ id, data }: { id: string; data: FormData }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.put(`${BASE_URL}/${id}`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return res.data as SubOrgResponse;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Update failed');
    }
  }
);


export const deleteSubOrg = createAsyncThunk(
  'subOrgs/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${BASE_URL}/${id}`);
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Delete failed');
    }
  }
);

// ---------- Slice ----------
const subOrgSlice = createSlice({
  name: 'subOrgs',
  initialState,
  reducers: {
    clearCreateState: (state) => {
      state.create = initialRequestState();
    },
    clearUpdateState: (state) => {
      state.update = initialRequestState();
    },
    clearDeleteState: (state) => {
      state.delete = initialRequestState();
    },
  },
  extraReducers: (builder) => {
    // Fetch all
    builder.addCase(fetchSubOrgsByParentID.pending, (state) => {
      state.list.loading = true;
      state.list.error = null;
    });
    builder.addCase(fetchSubOrgsByParentID.fulfilled, (state, action) => {
      state.list.loading = false;
      state.list.data = action.payload;
    });
    builder.addCase(fetchSubOrgsByParentID.rejected, (state, action) => {
      state.list.loading = false;
      state.list.error = action.payload as string;
    });

    // Fetch All (Global)
    builder.addCase(fetchAllSubOrgs.pending, (state) => {
      state.list.loading = true;
      state.list.error = null;
    });
    builder.addCase(fetchAllSubOrgs.fulfilled, (state, action) => {
      state.list.loading = false;
      state.list.data = action.payload.content; // Assuming we want the array in state.list.data
      // You might also want to store totalElements etc if needed for paging
    });
    builder.addCase(fetchAllSubOrgs.rejected, (state, action) => {
      state.list.loading = false;
      state.list.error = action.payload as string;
    });

    // Fetch by ID
    builder.addCase(fetchSubOrgById.pending, (state) => {
      state.single.loading = true;
      state.single.error = null;
    });
    builder.addCase(fetchSubOrgById.fulfilled, (state, action) => {
      state.single.loading = false;
      state.single.data = action.payload;
    });
    builder.addCase(fetchSubOrgById.rejected, (state, action) => {
      state.single.loading = false;
      state.single.error = action.payload as string;
    });

    // POS Config Fields
    builder.addCase(fetchPosConfigFields.pending, (state) => {
      state.posConfigFields.loading = true;
      state.posConfigFields.error = null;
    });
    builder.addCase(fetchPosConfigFields.fulfilled, (state, action) => {
      state.posConfigFields.loading = false;
      state.posConfigFields.data = action.payload;
    });
    builder.addCase(fetchPosConfigFields.rejected, (state, action) => {
      state.posConfigFields.loading = false;
      state.posConfigFields.error = action.payload as string;
    });

    // POS Config Data
    builder.addCase(fetchPosConfig.pending, (state) => {
      state.posConfig.loading = true;
      state.posConfig.error = null;
    });
    builder.addCase(fetchPosConfig.fulfilled, (state, action) => {
      state.posConfig.loading = false;
      state.posConfig.data = action.payload;
    });
    builder.addCase(fetchPosConfig.rejected, (state, action) => {
      state.posConfig.loading = false;
      state.posConfig.error = action.payload as string;
    });

    // Save POS Config
    builder.addCase(savePosConfig.pending, (state) => {
      state.savePosConfig.loading = true;
      state.savePosConfig.error = null;
    });
    builder.addCase(savePosConfig.fulfilled, (state, action) => {
      state.savePosConfig.loading = false;
      state.savePosConfig.data = action.payload;
      // Optimistic upate if config affects single org
      if (state.single.data) {
        state.single.data.organisationPOS = action.payload.posType || state.single.data.organisationPOS;
        state.single.data.posSyncStatus = action.payload.syncStatus || 'CONFIGURED';
      }
    });
    builder.addCase(savePosConfig.rejected, (state, action) => {
      state.savePosConfig.loading = false;
      state.savePosConfig.error = action.payload as string;
    });

    // Sync POS
    builder.addCase(startPosSync.pending, (state) => {
      state.syncPos.loading = true;
      state.syncPos.error = null;
    });
    builder.addCase(startPosSync.fulfilled, (state, action) => {
      state.syncPos.loading = false;
      state.syncPos.data = action.payload;
    });
    builder.addCase(startPosSync.rejected, (state, action) => {
      state.syncPos.loading = false;
      state.syncPos.error = action.payload as string;
    });

    // Pull from POS
    builder.addCase(pullFromPos.pending, (state) => {
      state.pullPos.loading = true;
      state.pullPos.error = null;
    });
    builder.addCase(pullFromPos.fulfilled, (state, action) => {
      state.pullPos.loading = false;
      state.pullPos.data = action.payload;
    });
    builder.addCase(pullFromPos.rejected, (state, action) => {
      state.pullPos.loading = false;
      state.pullPos.error = action.payload as string;
    });

    // Push to POS
    builder.addCase(pushToPos.pending, (state) => {
      state.pushPos.loading = true;
      state.pushPos.error = null;
    });
    builder.addCase(pushToPos.fulfilled, (state, action) => {
      state.pushPos.loading = false;
      state.pushPos.data = action.payload;
    });
    builder.addCase(pushToPos.rejected, (state, action) => {
      state.pushPos.loading = false;
      state.pushPos.error = action.payload as string;
    });

    // Create
    builder.addCase(createSubOrg.pending, (state) => {
      state.create.loading = true;
      state.create.error = null;
    });
    builder.addCase(createSubOrg.fulfilled, (state, action) => {
      state.create.loading = false;
      state.create.data = action.payload;
    });
    builder.addCase(createSubOrg.rejected, (state, action) => {
      state.create.loading = false;
      state.create.error = action.payload as string;
    });

    // Update
    builder.addCase(updateSubOrg.pending, (state) => {
      state.update.loading = true;
      state.update.error = null;
    });
    builder.addCase(updateSubOrg.fulfilled, (state, action) => {
      state.update.loading = false;
      state.update.data = action.payload;
    });
    builder.addCase(updateSubOrg.rejected, (state, action) => {
      state.update.loading = false;
      state.update.error = action.payload as string;
    });

    // Delete
    builder.addCase(deleteSubOrg.pending, (state) => {
      state.delete.loading = true;
      state.delete.error = null;
    });
    builder.addCase(deleteSubOrg.fulfilled, (state, action) => {
      state.delete.loading = false;
      state.delete.data = action.payload;
    });
    builder.addCase(deleteSubOrg.rejected, (state, action) => {
      state.delete.loading = false;
      state.delete.error = action.payload as string;
    });
  },
});

export const { clearCreateState, clearUpdateState, clearDeleteState } = subOrgSlice.actions;
export default subOrgSlice.reducer;
